(() => {
  if (customElements.get('rnf-account-portal')) return;
  const albumKey = 'rnf-account-album-demo-v1';
  const identifier = value => typeof value === 'string' && value.trim() ? value.trim() : Number.isSafeInteger(value) && value > 0 ? String(value) : '';
  customElements.define('rnf-account-portal', class extends HTMLElement {
    connectedCallback() {
      if (this.onClick) return;
      if (!this.initialized) {
        this.initialized = true;
        try { this.members = JSON.parse(this.querySelector('[data-rnf-members-data]')?.textContent || '{}') || {}; } catch { this.members = {}; }
        this.catalogue = this.readCatalogue(this.members.products);
        this.catalogueById = new Map(this.catalogue.map(product => [product.model, product]));
        this.album = new Map();
        this.nextSerial = 1;
        this.selectedBoxes = new Set();
        this.loadAlbum();
        this.resetBoxes();
        this.renderAlbum();
      }
      this.topup(this.querySelector('[data-topup-amount][aria-pressed="true"]') || this.querySelector('[data-topup-amount]'));
      this.onClick = event => {
        const target = event.target.closest ? event.target : event.target.parentElement;
        if (!target) return;
        const opener = target.closest('[data-account-open]');
        if (opener) this.show(opener.dataset.accountOpen);
        const tab = target.closest('[data-account-tab]');
        if (tab) this.select(tab.dataset.accountTab);
        const frequency = target.closest('[data-account-frequency]');
        if (frequency) this.querySelectorAll('[data-account-frequency]').forEach(button => button.setAttribute('aria-pressed', String(button === frequency)));
        const amount = target.closest('[data-topup-amount]');
        if (amount) this.topup(amount);
        if (target.closest('[data-open-case]')) this.openCase();
        const box = target.closest('[data-box-index]');
        if (box) this.toggleBox(Number(box.dataset.boxIndex));
        if (target.closest('[data-reveal-boxes]')) this.revealBoxes();
        if (target.closest('[data-reset-boxes]')) { this.resetBoxes(); this.focusCase(); }
        if (target.closest('[data-reset-album]')) this.resetAlbum();
        const card = target.closest('[data-album-card]');
        if (card) this.openCard(card.dataset.albumCard, card);
        if (target.closest('[data-close-card]')) this.closeCard();
      };
      this.onChange = event => {
        if (event.target.matches('[data-account-language]')) event.target.form?.requestSubmit();
        if (event.target.matches('[data-demo-colour]')) this.changeColour(event.target);
      };
      this.film = this.querySelector('[data-rnf-film]');
      this.onFilmTime = () => {
        const seconds = this.film.currentTime;
        const index = seconds < 6 ? 0 : seconds < 12 ? 1 : seconds < 18 ? 2 : 3;
        const caption = this.querySelector('[data-film-caption]');
        if (caption) caption.textContent = this.members.captions?.[index] || '';
      };
      this.film?.addEventListener('timeupdate', this.onFilmTime);
      this.onTilt = event => {
        if (matchMedia('(prefers-reduced-motion: reduce), (pointer: coarse)').matches) return;
        const card = event.target.closest('[data-rnf-holo]');
        if (!card) return;
        const rect = card.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        card.style.setProperty('--foil-x', `${Math.max(0, Math.min(100, (event.clientX - rect.left) / rect.width * 100))}%`);
        card.style.setProperty('--foil-y', `${Math.max(0, Math.min(100, (event.clientY - rect.top) / rect.height * 100))}%`);
      };
      this.onStorage = event => {
        if (event.key !== albumKey) return;
        this.loadAlbum(event.newValue);
        this.closeCard();
        this.resetBoxes();
        this.renderAlbum();
      };
      this.dialog = this.querySelector('[data-card-dialog]');
      this.onDialogClose = () => {
        this.dialogModel = '';
        if (this.dialogOpener?.isConnected) this.dialogOpener.focus({preventScroll: true});
        this.dialogOpener = null;
      };
      this.dialog?.addEventListener('close', this.onDialogClose);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('pointermove', this.onTilt, {passive: true});
      window.addEventListener('storage', this.onStorage);
    }
    text(key) { return typeof this.members.copy?.[key] === 'string' ? this.members.copy[key] : ''; }
    readCatalogue(products) {
      const models = new Map();
      for (const product of Array.isArray(products) ? products : []) {
        const model = identifier(product?.model);
        if (!model || typeof product.title !== 'string') continue;
        let entry = models.get(model);
        if (!entry) {
          entry = {model, title: product.title, variants: [], slot: models.size + 1};
          models.set(model, entry);
        }
        for (const variant of Array.isArray(product.variants) ? product.variants : []) {
          const id = identifier(variant?.id);
          if (!id || entry.variants.some(item => item.id === id)) continue;
          let image = '';
          try {
            const url = new URL(typeof variant.image === 'string' ? variant.image : '', location.href);
            if (variant.image && ['https:', 'http:'].includes(url.protocol)) image = url.href;
          } catch {}
          entry.variants.push({id, label: typeof variant.label === 'string' ? variant.label : '', image});
        }
      }
      return [...models.values()].sort((left, right) => {
        let a = left.model;
        let b = right.model;
        if (/^\d+$/.test(a) && /^\d+$/.test(b)) {
          a = a.replace(/^0+(?=\d)/, '');
          b = b.replace(/^0+(?=\d)/, '');
          if (a.length !== b.length) return a.length - b.length;
        }
        return a < b ? -1 : a > b ? 1 : 0;
      }).map((product, index) => ({...product, slot: index + 1}));
    }
    loadAlbum(raw) {
      if (raw === undefined) { try { raw = localStorage.getItem(albumKey); } catch { return; } }
      let data;
      try { data = raw ? JSON.parse(raw) : null; } catch { data = null; }
      const entries = new Map();
      const serials = new Set();
      let nextSerial = 1;
      if (data?.version === 1 && Array.isArray(data.entries)) {
        for (const entry of data.entries.slice(0, this.catalogue.length * 2)) {
          const model = identifier(entry?.model);
          const product = this.catalogueById.get(model);
          const serial = entry?.serial;
          if (!product || entries.has(model) || !Number.isSafeInteger(serial) || serial < 1 || serial >= 1000000000 || serials.has(serial)) continue;
          const variantId = identifier(entry.variant);
          const variant = product.variants.find(item => item.id === variantId) || product.variants[0];
          entries.set(model, {model, variant: variant?.id || '', serial});
          serials.add(serial);
          nextSerial = Math.max(nextSerial, serial + 1);
        }
        if (Number.isSafeInteger(data.nextSerial) && data.nextSerial >= nextSerial && data.nextSerial < 1000000000) nextSerial = data.nextSerial;
      }
      this.album = entries;
      this.nextSerial = nextSerial;
    }
    saveAlbum() {
      // Local demo data is editable by the visitor and never authorises money, orders or ownership.
      try { localStorage.setItem(albumKey, JSON.stringify({version: 1, nextSerial: this.nextSerial, entries: [...this.album.values()]})); } catch {}
    }
    remainingModels() { return this.catalogue.filter(product => product.variants.length && !this.album.has(product.model)); }
    show(view) {
      const panel = view === 'login' ? 'login' : 'dashboard';
      this.dataset.view = panel;
      this.querySelectorAll('[data-account-panel]').forEach(element => { element.hidden = element.dataset.accountPanel !== panel; });
      this.querySelector(`[data-account-focus="${panel}"]`)?.focus({preventScroll: true});
      if (panel === 'dashboard') this.select('wallet');
      else this.film?.pause();
    }
    select(name) {
      if (!['wallet', 'boxes', 'album'].includes(name)) return;
      this.querySelectorAll('[data-account-tab]').forEach(button => {
        const selected = button.dataset.accountTab === name;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      this.querySelectorAll('[data-account-content]').forEach(element => { element.hidden = element.dataset.accountContent !== name; });
      if (name !== 'boxes') this.film?.pause();
    }
    topup(button) {
      if (!button) return;
      this.querySelectorAll('[data-topup-amount]').forEach(item => item.setAttribute('aria-pressed', String(item === button)));
      for (const [selector, value] of [['[data-topup-card]', button.dataset.topupTotal], ['[data-topup-deposit]', button.dataset.topupAmount], ['[data-topup-extra]', button.dataset.topupBonus]]) {
        const output = this.querySelector(selector);
        if (output) output.textContent = value;
      }
    }
    resetBoxes() {
      this.revealed = false;
      this.caseOpened = false;
      this.selectedBoxes.clear();
      this.roundEntries = new Map();
      this.recentModels = new Set();
      this.querySelectorAll('.rnf-album-slot.is-new').forEach(slot => slot.classList.remove('is-new'));
      const results = this.querySelector('[data-box-results]');
      if (results) { results.hidden = true; results.replaceChildren(); }
      this.syncBoxes();
    }
    focusCase() {
      const opener = this.querySelector('[data-open-case]');
      if (opener && !opener.disabled) opener.focus({preventScroll: true});
      else {
        const status = this.querySelector('[data-box-status]');
        if (status) { status.tabIndex = -1; status.focus({preventScroll: true}); }
      }
    }
    openCase() {
      if (this.caseOpened || this.revealed || this.remainingModels().length < 2) return;
      this.caseOpened = true;
      this.syncBoxes();
      this.querySelector('[data-box-index]')?.focus({preventScroll: true});
    }
    toggleBox(index) {
      if (!this.caseOpened || this.revealed || this.remainingModels().length < 2 || !Number.isInteger(index) || index < 0 || index > 3) return;
      if (this.selectedBoxes.has(index)) this.selectedBoxes.delete(index);
      else if (this.selectedBoxes.size < 2) this.selectedBoxes.add(index);
      this.syncBoxes();
    }
    syncBoxes() {
      const available = this.remainingModels().length >= 2;
      const intro = this.querySelector('[data-case-intro]');
      if (intro) intro.hidden = this.caseOpened;
      const stage = this.querySelector('[data-pack-stage]');
      if (stage) stage.hidden = !this.caseOpened;
      const open = this.querySelector('[data-open-case]');
      if (open) open.disabled = !available;
      this.querySelectorAll('[data-box-index]').forEach(button => {
        const selected = this.selectedBoxes.has(Number(button.dataset.boxIndex));
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('is-selected', selected);
        button.classList.toggle('is-opened', this.revealed && selected);
        button.classList.toggle('is-closed', this.revealed && !selected);
        button.disabled = !this.caseOpened || this.revealed || !available;
      });
      const reveal = this.querySelector('[data-reveal-boxes]');
      if (reveal) { reveal.hidden = !this.caseOpened || this.revealed; reveal.disabled = this.selectedBoxes.size !== 2 || !available || this.revealed; }
      const reset = this.querySelector('[data-reset-boxes]');
      if (reset) reset.hidden = !this.revealed;
      const status = this.querySelector('[data-box-status]');
      if (status) {
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.textContent = this.revealed ? `${this.text('revealed')} · ${this.text('added')} 2` : !available ? this.text(this.catalogue.length && this.album.size === this.catalogue.length ? 'complete' : 'unavailable') : !this.caseOpened ? this.text('openCase') : `${this.text('select')} · ${this.selectedBoxes.size} / 2`;
      }
    }
    revealBoxes() {
      if (!this.caseOpened || this.revealed || this.selectedBoxes.size !== 2) return;
      const remaining = this.remainingModels();
      const results = this.querySelector('[data-box-results]');
      if (remaining.length < 2 || !results || this.nextSerial >= 999999998) return;
      this.revealed = true;
      for (let index = 0; index < 2; index++) {
        const chosen = index + Math.floor(Math.random() * (remaining.length - index));
        [remaining[index], remaining[chosen]] = [remaining[chosen], remaining[index]];
      }
      [...this.selectedBoxes].sort((a, b) => a - b).forEach((packIndex, index) => {
        const product = remaining[index];
        const entry = {model: product.model, variant: product.variants[0].id, serial: this.nextSerial++};
        this.album.set(entry.model, entry);
        this.roundEntries.set(packIndex, entry.model);
        this.recentModels.add(entry.model);
      });
      this.saveAlbum();
      results.replaceChildren();
      for (const [packIndex, model] of this.roundEntries) results.append(this.createResult(this.catalogueById.get(model), this.album.get(model), packIndex));
      results.hidden = false;
      this.syncBoxes();
      this.renderAlbum();
      results.tabIndex = -1;
      results.focus({preventScroll: true});
      results.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'});
    }
    createCard(product, entry) {
      const card = document.createElement('article');
      card.className = 'rnf-collectible';
      card.dataset.rnfHolo = '';
      card.dataset.cardModel = product.model;
      const top = document.createElement('div');
      top.className = 'rnf-collectible__top';
      const logo = this.querySelector('template[data-card-logo]');
      if (logo) top.append(logo.content.cloneNode(true));
      const slot = document.createElement('span');
      slot.textContent = `#${String(product.slot).padStart(3, '0')}`;
      slot.setAttribute('aria-label', `${this.text('slot')} ${product.slot}`.trim());
      top.append(slot);
      const art = document.createElement('div');
      art.className = 'rnf-collectible__art';
      const variant = product.variants.find(item => item.id === entry.variant) || product.variants[0];
      if (variant?.image) {
        const image = document.createElement('img');
        image.src = variant.image;
        image.alt = `${product.title} ${variant.label}`.trim();
        image.width = 700; image.height = 700; image.loading = 'lazy'; image.decoding = 'async';
        art.append(image);
      } else { art.classList.add('is-unavailable'); art.setAttribute('aria-label', this.text('unavailable')); }
      const body = document.createElement('div');
      body.className = 'rnf-collectible__body';
      const title = document.createElement('h3');
      title.textContent = product.title;
      body.append(title);
      const serial = document.createElement('div');
      serial.className = 'rnf-collectible__serial';
      const serialLabel = document.createElement('small');
      serialLabel.textContent = this.text('series');
      const serialValue = document.createElement('span');
      serialValue.textContent = `DEMO-${String(entry.serial).padStart(6, '0')}`;
      serial.append(serialLabel, serialValue);
      const footer = document.createElement('div');
      footer.className = 'rnf-collectible__footer';
      const badge = document.createElement('span');
      badge.textContent = this.text('albumDemo');
      footer.append(badge);
      card.append(top, art, body, serial, footer);
      return card;
    }
    createResult(product, entry, packIndex) {
      const result = document.createElement('article');
      result.className = 'rnf-result is-chosen';
      result.dataset.resultIndex = packIndex;
      result.dataset.resultModel = product.model;
      const state = document.createElement('p');
      state.className = 'rnf-result__state';
      state.textContent = `${this.text('pack')} ${packIndex + 1} / ${this.text('selected')}`;
      result.append(state, this.createCard(product, entry));
      if (product.variants.length > 1) {
        const label = document.createElement('label');
        label.textContent = this.text('colour');
        const select = document.createElement('select');
        select.dataset.demoColour = String(packIndex);
        select.dataset.colourModel = product.model;
        product.variants.forEach(variant => select.add(new Option(variant.label, variant.id)));
        select.value = entry.variant;
        label.append(select);
        result.append(label);
      }
      const hint = document.createElement('p');
      hint.className = 'rnf-result__hint';
      hint.textContent = this.text(product.variants.length > 1 ? 'same' : 'single');
      result.append(hint);
      return result;
    }
    changeColour(select) {
      const index = Number(select.dataset.demoColour);
      const model = this.roundEntries.get(index);
      const result = select.closest('[data-result-model]');
      if (!this.revealed || !this.selectedBoxes.has(index) || model !== select.dataset.colourModel || result?.dataset.resultModel !== model) return;
      const product = this.catalogueById.get(model);
      const variant = product?.variants.find(item => item.id === select.value);
      const entry = this.album.get(model);
      if (!variant || !entry) return;
      entry.variant = variant.id;
      result.querySelector('.rnf-collectible')?.replaceWith(this.createCard(product, entry));
      this.saveAlbum();
      this.renderAlbum();
      if (this.dialogModel === model) this.renderCardDetail(model);
    }
    renderAlbum() {
      const grid = this.querySelector('[data-album-grid]');
      if (grid) {
        const fragment = document.createDocumentFragment();
        for (const product of this.catalogue) {
          const entry = this.album.get(product.model);
          const slot = document.createElement('div');
          slot.className = `rnf-album-slot${entry ? this.recentModels.has(product.model) ? ' is-new' : '' : ' is-locked'}`;
          slot.dataset.albumModel = product.model;
          if (entry) {
            const button = document.createElement('button');
            button.type = 'button';
            button.dataset.albumCard = product.model;
            button.setAttribute('aria-label', `${this.text('openCard')} ${product.title}`.trim());
            button.append(this.createCard(product, entry));
            slot.append(button);
          } else {
            const index = document.createElement('span');
            index.className = 'rnf-album-slot__number';
            index.textContent = `#${String(product.slot).padStart(3, '0')}`;
            const placeholder = document.createElement('span');
            placeholder.className = 'rnf-album-slot__placeholder';
            placeholder.setAttribute('aria-hidden', 'true');
            placeholder.textContent = '+';
            const title = document.createElement('h3');
            title.textContent = product.title;
            const locked = document.createElement('span');
            locked.className = 'rnf-album-slot__locked';
            locked.textContent = this.text('locked');
            slot.append(index, placeholder, title, locked);
          }
          fragment.append(slot);
        }
        grid.replaceChildren(fragment);
      }
      this.querySelectorAll('[data-album-count]').forEach(count => { count.textContent = `${this.album.size} / ${this.catalogue.length}`; });
      this.querySelectorAll('[data-album-progress]').forEach(progress => { progress.max = Math.max(1, this.catalogue.length); progress.value = this.album.size; progress.setAttribute('aria-valuetext', `${this.album.size} / ${this.catalogue.length}`); });
      this.querySelectorAll('[data-album-empty]').forEach(empty => { empty.hidden = this.album.size > 0; });
    }
    resetAlbum() {
      this.closeCard();
      this.album.clear();
      this.nextSerial = 1;
      try { localStorage.removeItem(albumKey); } catch {}
      this.resetBoxes();
      this.renderAlbum();
      const count = this.querySelector('[data-account-content="album"] [data-album-count]');
      if (count) { count.tabIndex = -1; count.focus({preventScroll: true}); }
    }
    renderCardDetail(model) {
      const product = this.catalogueById.get(model);
      const entry = this.album.get(model);
      const detail = this.querySelector('[data-card-detail]');
      if (!product || !entry || !detail) return false;
      const card = this.createCard(product, entry);
      const title = card.querySelector('h3');
      title.tabIndex = -1;
      detail.replaceChildren(card);
      this.dialog?.setAttribute('aria-label', `${this.text('openCard')} ${product.title}`.trim());
      return true;
    }
    openCard(model, opener) {
      if (!this.dialog || !this.renderCardDetail(model)) return;
      this.dialogModel = model;
      this.dialogOpener = opener;
      if (!this.dialog.open) {
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
      }
      this.dialog.querySelector('h3')?.focus({preventScroll: true});
    }
    closeCard() {
      if (!this.dialog?.open) return;
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else { this.dialog.removeAttribute('open'); this.onDialogClose?.(); }
    }
    disconnectedCallback() {
      this.closeCard();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('pointermove', this.onTilt);
      window.removeEventListener('storage', this.onStorage);
      this.dialog?.removeEventListener('close', this.onDialogClose);
      this.film?.removeEventListener('timeupdate', this.onFilmTime);
      this.film?.pause();
      this.onClick = null;
    }
  });
})();
